import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import QRCode from "qrcode";
import { Search, MessageCircle, BadgeCheck, ArrowLeft, Share, PlusSquare, Home, Info, CheckCircle2, Smartphone } from "lucide-react";
import {
  subscribePwa,
  triggerInstallPrompt,
  isIos,
  isAndroid,
  isMobile,
  isChromeOnIos,
  isPwaInstalled,
} from "@/lib/pwaInstall";
import { trackEvent } from "@/lib/analytics";

const INSTALL_URL = "https://lokasos.com/app";

const META = {
  title: "Installer Lokaso — Application gratuite | Annuaire des entreprises de Guinée",
  description:
    "10 000+ entreprises à Conakry. Installez l'appli gratuite en 1 clic et contactez directement sur WhatsApp.",
  ogImage: "https://lokasos.com/og-install.jpg",
};

// Ensure a <meta> tag exists (create if missing) and update its content.
function upsertMeta({ name, property, content }) {
  const selector = name ? `meta[name="${name}"]` : `meta[property="${property}"]`;
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    if (name) tag.setAttribute("name", name);
    if (property) tag.setAttribute("property", property);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
  return tag;
}

function useSocialMeta() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = META.title;
    const tags = [
      upsertMeta({ name: "description", content: META.description }),
      upsertMeta({ property: "og:title", content: META.title }),
      upsertMeta({ property: "og:description", content: META.description }),
      upsertMeta({ property: "og:type", content: "website" }),
      upsertMeta({ property: "og:url", content: INSTALL_URL }),
      upsertMeta({ property: "og:image", content: META.ogImage }),
      upsertMeta({ property: "og:image:width", content: "1200" }),
      upsertMeta({ property: "og:image:height", content: "630" }),
      upsertMeta({ property: "og:image:alt", content: "Installez Lokaso en 1 clic — 10 000+ entreprises · Guinée" }),
      upsertMeta({ property: "og:site_name", content: "Lokaso" }),
      upsertMeta({ property: "og:locale", content: "fr_FR" }),
      upsertMeta({ name: "twitter:card", content: "summary_large_image" }),
      upsertMeta({ name: "twitter:title", content: META.title }),
      upsertMeta({ name: "twitter:description", content: META.description }),
      upsertMeta({ name: "twitter:image", content: META.ogImage }),
    ];
    return () => {
      document.title = prevTitle;
      // leave tags in place; they're overwritten by other pages via the same helper
      void tags;
    };
  }, []);
}

function detectPlatform() {
  if (typeof window === "undefined") return "desktop";
  if (isIos()) return "ios";
  if (isAndroid() || isMobile()) return "android";
  return "desktop";
}

const Benefits = () => (
  <ul className="flex flex-wrap justify-center gap-3 sm:gap-4 text-[#1C1917]/85 text-sm">
    <li className="flex items-center gap-2 bg-white rounded-full px-3.5 py-1.5 shadow-sm ring-1 ring-black/5" data-testid="benefit-directory">
      <Search className="w-4 h-4 text-[#1A5C35]" />
      <span>10 000+ entreprises</span>
    </li>
    <li className="flex items-center gap-2 bg-white rounded-full px-3.5 py-1.5 shadow-sm ring-1 ring-black/5" data-testid="benefit-whatsapp">
      <MessageCircle className="w-4 h-4 text-[#1A5C35]" />
      <span>Contact WhatsApp direct</span>
    </li>
    <li className="flex items-center gap-2 bg-white rounded-full px-3.5 py-1.5 shadow-sm ring-1 ring-black/5" data-testid="benefit-free">
      <BadgeCheck className="w-4 h-4 text-[#1A5C35]" />
      <span>100% gratuit</span>
    </li>
  </ul>
);

// ─────────────────────────────────────────────────────────────────────────────
// State A — Android / Chromium

function AndroidState({ deferredPrompt, installed, onInstall }) {
  if (installed) {
    return (
      <div className="text-center" data-testid="install-state-installed">
        <div className="inline-flex items-center gap-2 bg-[#1A5C35] text-white px-4 py-3 rounded-full font-semibold">
          <CheckCircle2 className="w-5 h-5" />
          Lokaso est installé !
        </div>
        <p className="mt-4 text-[#1C1917]/75 text-sm max-w-sm mx-auto">
          Ouvrez Lokaso depuis votre écran d'accueil pour un accès instantané.
        </p>
      </div>
    );
  }

  if (deferredPrompt) {
    return (
      <div className="text-center" data-testid="install-state-android">
        <button
          type="button"
          onClick={onInstall}
          data-testid="android-install-button"
          className="inline-flex items-center justify-center gap-2 bg-[#E8A020] hover:bg-[#F0B040] active:bg-[#c4810f] text-[#1C1917] font-bold text-lg rounded-2xl px-8 py-4 w-full sm:w-auto sm:min-w-[280px] shadow-lg shadow-[#E8A020]/30 transition-all hover:scale-[1.02] active:scale-[0.99]"
        >
          <Smartphone className="w-5 h-5" />
          Installer Lokaso
        </button>
        <p className="mt-3 text-xs text-[#1C1917]/60">
          Installation instantanée · aucun store requis
        </p>
      </div>
    );
  }

  // Fallback: Chrome/Android without deferred event (already installed, unsupported, or event missed)
  return (
    <div className="rounded-2xl bg-white p-5 sm:p-6 shadow-sm ring-1 ring-black/5 text-left" data-testid="install-state-android-fallback">
      <p className="font-semibold text-[#1C1917]">Ajouter Lokaso manuellement</p>
      <ol className="mt-3 space-y-3 text-sm text-[#1C1917]/85">
        <li className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#1A5C35] text-white text-xs font-bold">1</span>
          <span>Ouvrez le menu <strong>⋮</strong> en haut à droite du navigateur</span>
        </li>
        <li className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#1A5C35] text-white text-xs font-bold">2</span>
          <span>Sélectionnez <strong>« Ajouter à l'écran d'accueil »</strong> ou <strong>« Installer l'application »</strong></span>
        </li>
        <li className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#1A5C35] text-white text-xs font-bold">3</span>
          <span>Confirmez avec <strong>« Installer »</strong> — l'icône Lokaso apparaîtra sur votre écran d'accueil</span>
        </li>
      </ol>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// State B — iPhone / iPad

function IosState() {
  const chromeOnIos = isChromeOnIos();
  useEffect(() => { trackEvent("install_page_ios_view", { source: "app_page" }); }, []);

  if (chromeOnIos) {
    return (
      <div className="rounded-2xl bg-[#FFF7E6] border border-[#E8A020]/50 p-5 sm:p-6" data-testid="ios-safari-redirect">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-[#B47800] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-[#1C1917]">
            <p className="font-semibold mb-1">Ouvrez cette page dans Safari</p>
            <p className="text-[#1C1917]/80">
              Sur iPhone/iPad, seul Safari permet d'installer Lokaso sur votre écran d'accueil. Copiez le lien <strong>lokasos.com/app</strong> et ouvrez-le dans Safari pour continuer.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const steps = [
    { icon: <Share className="w-7 h-7" />, title: "Appuyez sur Partager", desc: "Le bouton ⬆️ en bas de Safari" },
    { icon: <PlusSquare className="w-7 h-7" />, title: "Sélectionnez « Sur l'écran d'accueil »", desc: "Faites défiler la liste vers le bas si besoin" },
    { icon: <Home className="w-7 h-7" />, title: "Appuyez sur « Ajouter »", desc: "L'icône Lokaso apparaît sur votre écran d'accueil" },
  ];

  return (
    <div className="space-y-3" data-testid="install-state-ios">
      {steps.map((s, i) => (
        <div key={i} className="flex items-center gap-4 bg-white rounded-2xl p-4 sm:p-5 shadow-sm ring-1 ring-black/5">
          <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-[#1A5C35] text-white flex items-center justify-center">
            {s.icon}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#E8A020] text-[#1C1917] text-[11px] font-extrabold">{i + 1}</span>
              <p className="font-semibold text-[#1C1917] leading-tight">{s.title}</p>
            </div>
            <p className="text-sm text-[#1C1917]/70 mt-1">{s.desc}</p>
          </div>
        </div>
      ))}
      <p className="text-xs text-center text-[#1C1917]/60 pt-1">
        L'application se lance ensuite depuis l'écran d'accueil, comme n'importe quelle appli.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// State C — Desktop (QR code)

function DesktopState() {
  const [dataUrl, setDataUrl] = useState("");
  const canvasRef = useRef(null);

  useEffect(() => {
    QRCode.toDataURL(INSTALL_URL, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 320,
      color: { dark: "#1A5C35", light: "#FAF8F3" },
    }).then(setDataUrl).catch(() => setDataUrl(""));
  }, []);

  return (
    <div className="text-center" data-testid="install-state-desktop">
      <div className="inline-block bg-white p-4 rounded-3xl shadow-lg ring-1 ring-black/5">
        {dataUrl ? (
          <img
            ref={canvasRef}
            src={dataUrl}
            alt="QR code pour installer Lokaso"
            width={320}
            height={320}
            className="block"
            data-testid="install-qr-code"
          />
        ) : (
          <div className="w-[320px] h-[320px] flex items-center justify-center text-[#1C1917]/40 text-sm">
            Génération du QR code…
          </div>
        )}
      </div>
      <p className="mt-5 text-[#1C1917] font-semibold">
        Scannez ce QR code avec votre téléphone
      </p>
      <p className="mt-1 text-sm text-[#1C1917]/70">
        pour installer Lokaso en 1 clic
      </p>
      <p className="mt-6 text-xs text-[#1C1917]/50">
        Ou visitez <a href={INSTALL_URL} className="underline hover:text-[#1A5C35]" data-testid="install-desktop-url">{INSTALL_URL}</a> sur votre téléphone
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function InstallAppPage() {
  useSocialMeta();

  const [state, setState] = useState({ deferredPrompt: null, installed: isPwaInstalled() });
  useEffect(() => subscribePwa((s) => setState(s)), []);
  useEffect(() => { trackEvent("page_view", { page_title: META.title, page_path: "/app" }); }, []);

  const platform = useMemo(() => detectPlatform(), []);

  const handleInstall = async () => {
    trackEvent("install_button_click", { source: "app_page", platform });
    const outcome = await triggerInstallPrompt();
    if (outcome === "accepted") {
      trackEvent("pwa_installed", { source: "app_page" });
    }
  };

  return (
    <main className="min-h-screen bg-[#FAF8F3]" data-testid="install-app-page">
      {/* Green header block */}
      <section className="bg-[#1A5C35] text-[#FAF8F3] relative overflow-hidden">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-6 pb-10 sm:pt-8 sm:pb-14">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-[#FAF8F3]/80 hover:text-[#FAF8F3] transition-colors"
            data-testid="install-back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au site
          </Link>

          <div className="mt-6 sm:mt-8 flex items-center gap-3">
            <span className="lokaso-flag-stripe h-8"><span /><span /><span /></span>
            <span className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">LOKASO</span>
          </div>

          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">
            Installez Lokaso <br className="hidden sm:block" />sur votre téléphone
          </h1>
          <p className="mt-3 text-[#FAF8F3]/85 text-base sm:text-lg max-w-xl">
            Gratuit · 10 000+ entreprises · Accès en 1 clic
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 -mt-6 sm:-mt-8 relative z-10">
        <div className="bg-[#FAF8F3] pt-6 sm:pt-8">
          {platform === "android" && (
            <AndroidState
              deferredPrompt={state.deferredPrompt}
              installed={state.installed}
              onInstall={handleInstall}
            />
          )}
          {platform === "ios" && <IosState />}
          {platform === "desktop" && <DesktopState />}
        </div>

        {/* Benefits row */}
        <div className="mt-10 sm:mt-12">
          <Benefits />
        </div>

        {/* Trust line */}
        <p className="mt-8 mb-12 text-center text-xs text-[#1C1917]/50">
          Aucun compte requis · Fonctionne hors ligne · Made in Guinée 🇬🇳
        </p>
      </section>
    </main>
  );
}
